import React, { useState } from 'react'
import { Star, Truck, Tag, Circle } from 'lucide-react'
import {
	ProductEntity,
	attributeMaps,
	NameAttributes,
} from '@/entities/api/products/product'
import { API_URL } from '@/services/api/keys'
import { Entities } from '@/entities/api/entities'

interface SearchProductCardProps {
	product: ProductEntity
	className?: string
	click: () => void
}

const ProductCardSearch: React.FC<SearchProductCardProps> = ({
	product,
	className = '',
	click,
}) => {
	const [selectedAttribute, setSelectedAttribute] = useState(0)
	const [isHovered, setIsHovered] = useState(false)

	const hasDiscount = product.discount > 0
	const hasVariants = product.attributes && product.attributes.length > 0
	const hasFreeShipping = product.shipping_info.type === 'free'

	const currentAttribute =
		hasVariants && product.attributes?.[selectedAttribute]
			? product.attributes[selectedAttribute]
			: null

	const formatCurrency = (price: number): string => {
		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
		}).format(price)
	}

	const getAttributePrice = (): number => {
		return currentAttribute?.price || 45000
	}

	const calculateDiscountedPrice = (
		price: number,
		discount: number
	): number => {
		return price - (price * discount) / 100
	}

	const basePrice = getAttributePrice()
	const discountedPrice = hasDiscount
		? calculateDiscountedPrice(basePrice, product.discount)
		: basePrice

	const getCurrentImage = (): string => {
		if (currentAttribute?.image?.url) {
			return `${API_URL}/${currentAttribute.image.url}`
		}
		if (product.images?.[0]?.url) {
			return `${API_URL}/${product.images[0].url}`
		}
		return 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop'
	}

	const getAverageRating = () => {
		if (!product.raitings || product.raitings.length === 0) {
			return { average: 0, count: 0 }
		}

		const validRatings = product.raitings.filter(
			(rating) => !rating._deleted && rating.rate
		)
		const sum = validRatings.reduce((acc, rating) => acc + rating.rate, 0)
		const average = validRatings.length > 0 ? sum / validRatings.length : 0

		return {
			average: Math.round(average * 10) / 10,
			count: validRatings.length,
		}
	}

	const { average: averageRating, count: reviewCount } = getAverageRating()

	const renderAttributeVariant = (attr: any, index: number) => {
		const isSelected = selectedAttribute === index

		if (
			attr.name === NameAttributes.COLOR &&
			attributeMaps[attr.value as keyof typeof attributeMaps]
		) {
			const colorValue = attributeMaps[attr.value as keyof typeof attributeMaps]
			return (
				<button
					key={attr._id || index}
					onClick={() => setSelectedAttribute(index)}
					className={`relative w-7 h-7 sm:w-10 sm:h-10 rounded-full border-2 transition-all duration-200 ${
						isSelected
							? 'border-blue-500 ring-1 ring-blue-200 scale-105 sm:scale-110'
							: 'border-gray-300 hover:border-gray-400 hover:scale-105'
					}`}
					style={{ backgroundColor: colorValue }}
					title={attr.value}
					aria-label={`Color ${attr.value}`}
				>
					{isSelected && (
						<div className='absolute inset-0 rounded-full border-2 border-white flex items-center justify-center'>
							<Circle size={4} className='sm:hidden text-white fill-current' />
							<Circle
								size={6}
								className='hidden sm:block text-white fill-current'
							/>
						</div>
					)}
				</button>
			)
		}

		return (
			<button
				key={attr._id || index}
				onClick={() => setSelectedAttribute(index)}
				className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded border transition-all duration-200 text-xs font-medium ${
					isSelected
						? 'border-blue-500 bg-blue-50 text-blue-700'
						: 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
				}`}
				title={attr.value}
			>
				{attr.value.toString().slice(0, 4)}
			</button>
		)
	}

	return (
		<div
			onClick={click}
			className={`cursor-pointer mb-4 group relative bg-white rounded-lg sm:rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 ${className}`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className='flex flex-col sm:flex-row md:flex-row'>
				<div className='relative w-full sm:w-32 md:w-48 lg:w-56 aspect-square sm:aspect-auto overflow-hidden bg-gray-50'>
					<img
						src={getCurrentImage()}
						alt={product.name}
						className={`w-full h-full object-cover transition-transform duration-500 ${
							isHovered ? 'scale-105' : 'scale-100'
						}`}
						loading='lazy'
					/>

					<div className='absolute top-1 left-1 sm:top-2 sm:left-2 flex flex-col gap-0.5 sm:gap-1'>
						{hasDiscount && (
							<div className='bg-red-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs font-bold shadow-md'>
								-{product.discount}%
							</div>
						)}

						{product.type !== Entities.PLANS_PERSONALIZED &&
							hasFreeShipping && (
								<div className='bg-green-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs font-medium flex items-center gap-0.5 sm:gap-1'>
									<Truck size={8} className='sm:hidden' />
									<Truck size={10} className='hidden sm:block' />
									<span className='hidden xs:inline text-xs'>Gratis</span>
								</div>
							)}
					</div>

					<div className='absolute top-1 right-1 sm:top-2 sm:right-2 bg-black/75 text-white px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-xs'>
						<Tag size={8} className='inline mr-0.5 sm:hidden' />
						<Tag size={10} className='hidden sm:inline mr-1' />
						<span className='text-xs'>{product.category.name.slice(0, 6)}</span>
					</div>

					<div className='absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300' />
				</div>

				<div className='flex-1 p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3'>
					<div className='space-y-1'>
						<div className='flex items-start justify-between gap-1 sm:gap-2'>
							<div className='flex-1 min-w-0'>
								<span className='text-xs font-medium text-gray-500 uppercase tracking-wide block truncate'>
									{product.brand}
								</span>
								<h3 className='font-semibold text-gray-900 text-xs sm:text-sm leading-tight line-clamp-2 mt-0.5 sm:mt-1'>
									{product.name}
								</h3>
							</div>

							{reviewCount > 0 && (
								<div className='flex items-center gap-0.5 sm:gap-1 shrink-0'>
									<Star
										size={10}
										className='sm:hidden text-yellow-400 fill-current'
									/>
									<Star
										size={12}
										className='hidden sm:block text-yellow-400 fill-current'
									/>
									<span className='text-xs text-gray-600 font-medium'>
										{averageRating}
									</span>
									<span className='text-xs text-gray-400 hidden sm:inline'>
										({reviewCount})
									</span>
								</div>
							)}
						</div>
					</div>

					{hasVariants && (
						<div className='space-y-1 sm:space-y-2'>
							<div className='flex items-center gap-1 sm:gap-2 flex-wrap'>
								{product.attributes
									?.slice(0, 3)
									.map((attr, index) => renderAttributeVariant(attr, index))}
								{product.attributes && product.attributes.length > 3 && (
									<span className='text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded'>
										+{product.attributes.length - 3}
									</span>
								)}
							</div>
						</div>
					)}

					<div className='space-y-1.5 sm:space-y-2 pt-1.5 sm:pt-2 border-t border-gray-100'>
						<div className='flex items-baseline gap-1 sm:gap-2 flex-wrap'>
							{hasDiscount && (
								<span className='text-xs sm:text-sm text-gray-400 line-through'>
									{formatCurrency(basePrice)}
								</span>
							)}
							<span
								className={`text-sm sm:text-lg font-bold ${
									hasDiscount ? 'text-red-600' : 'text-gray-900'
								}`}
							>
								{formatCurrency(discountedPrice)}
							</span>
						</div>

						<div className='flex items-center justify-between text-xs'>
							{product?.type !== Entities.PLANS_PERSONALIZED && (
								<div className='flex items-center gap-0.5 sm:gap-1'>
									<Truck
										size={10}
										className={
											hasFreeShipping ? 'text-green-600' : 'text-gray-500'
										}
									/>
									<span
										className={`text-xs ${
											hasFreeShipping ? 'text-green-600' : 'text-gray-600'
										}`}
									>
										{hasFreeShipping
											? 'Gratis'
											: formatCurrency(product.shipping_info.price)}
									</span>
								</div>
							)}

							{hasVariants && currentAttribute && (
								<div className='flex items-center gap-0.5 sm:gap-1'>
									<div
										className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${
											currentAttribute.quantity > 10
												? 'bg-green-500'
												: currentAttribute.quantity > 0
												? 'bg-orange-500'
												: 'bg-red-500'
										}`}
									/>
									<span
										className={`text-xs ${
											currentAttribute.quantity > 10
												? 'text-green-600'
												: currentAttribute.quantity > 0
												? 'text-orange-600'
												: 'text-red-600'
										}`}
									>
										{currentAttribute.quantity > 10
											? 'Stock'
											: currentAttribute.quantity > 0
											? `${currentAttribute.quantity}`
											: 'Agotado'}
									</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default ProductCardSearch
